"use client"

import { useState, useRef, useEffect, Suspense } from "react"
import classNames from "classnames"
import dynamic from "next/dynamic"

import NoJavascript from "../no-javascript/no-javascript"
import FormSubmit from "../form-submit/form-submit"

import { FormStatus, FormErrorCodes } from "@/lib/form-constants"
import { checkFormError, formError } from "@/lib/form-validator"

import styles from'./contact-form.module.scss'

const InvisibleReCAPTCHA = dynamic(
  () => import("../invisible-recaptcha/invisible-recaptcha"),
  { ssr: false }
)

export default function ContactForm({ className, data }) {
  const [formData, setFormData] = useState({});
  const [recaptchaNeeded, setRecaptchaNeeded] = useState(false)
  const [formStatus, setFormStatus] = useState(FormStatus.IDLE);
  const [error, setError] = useState(null);
  const recaptchaRef = useRef()

  useEffect(() => {
    setFormData(
      data.fields.reduce((object, field) => {
        object[field.name] = fieldData(field, '')

        return object
      }, {})
    )
  }, [data])

  const handleFieldChange = (field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [field.name]: fieldData(field, value)
    }))

    setRecaptchaNeeded(true)
  }

  const fieldData = (field, value) => ({
    label: field.label,
    required: field.required,
    value
  })

  const errorMessage = (errorCode, label) => {
    switch (errorCode) {
      case FormErrorCodes.INVALID_EMAIL:
        return "Please input a valid email address"
      case FormErrorCodes.REQUIRED_FIELD_EMPTY:
        return `Please input your ${label}`
      default:
        return "There was an error sending your request. Please try again"
    }
  }

  const submitForm = async (event) => {
    event.preventDefault()

    if (formStatus !== FormStatus.IDLE) {
      return
    }

    const errorData = checkFormError(formData)
    if (errorData) {
      setError(errorData)
      return
    }

    setError(null)
    setFormStatus(FormStatus.SENDING)
    recaptchaRef.current.execute()
  }

  const onReCAPTCHAChange = async (captchaCode) => {
    if (captchaCode === null || captchaCode === undefined) {
      setError(formError(FormErrorCodes.UNABLE_TO_SUBMIT_FORM))
      return
    }

    try {
      const response = await fetch("/contact-us/api", {
        method: "POST",
        body: JSON.stringify({
          formData,
          captchaCode
        }),
        headers: {
          "Content-Type": "application/json"
        }
      })

      const responseData = await response.json()

      if (response.ok) {
        setFormStatus(FormStatus.SENT)
      }
      else {
        setError(responseData.message)
        setFormStatus(FormStatus.IDLE)
      }
    } catch (error) {
      setError(formError(FormErrorCodes.UNABLE_TO_SUBMIT_FORM))
      setFormStatus(FormStatus.IDLE)
    } finally {
      recaptchaRef.current.reset()
    }
  }

  return (
    <>
      {formStatus === FormStatus.SENT &&
        <p className={classNames(styles["success"], className)}>
          {"Thank you! Your submission has been received. We'll get back to you soon!"}
        </p>
      }
      {formStatus !== FormStatus.SENT &&
        <Suspense fallback={<NoJavascript />}>
          <form className={classNames(
            styles["contact-form"],
            className
          )}>
            {data.fields.map((field) => (
              <label key={field.name}>
                {field.label && (field.label + (field.required ? ' *' : ''))}
                {field.type === "textarea" &&
                  <textarea
                    className={field.className}
                    name={field.name}
                    placeholder={field.placeholder}
                    maxLength={field.maxLength}
                    required={field.required}
                    value={formData[field.name] ? formData[field.name].value : ''}
                    onChange={(event) => handleFieldChange(field, event.target.value)}
                  />
                }
                {field.type !== "textarea" &&
                  <input
                    className={field.className}
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    maxLength={field.maxLength}
                    autoComplete={field.autoComplete}
                    required={field.required}
                    value={formData[field.name] ? formData[field.name].value : ''}
                    onChange={(event) => handleFieldChange(field, event.target.value)}
                  />
                }
                { error && error.fieldName === field.name &&
                  <div className={styles["error"]}>
                    {errorMessage(error.errorCode, field.label)}
                  </div>
                }
              </label>
            ))}
            { error && !error.fieldName &&
              <div className={styles["error"]}>
                {errorMessage(error.errorCode)}
              </div>
            }
            {recaptchaNeeded &&
              <InvisibleReCAPTCHA
                innerRef={recaptchaRef}
                onChange={onReCAPTCHAChange}
              />
            }
            <FormSubmit
              className={classNames(data.submit.className)}
              value={data.submit.value}
              loadingValue={data.submit.loadingValue}
              loading={formStatus === FormStatus.SENDING}
              onClick={submitForm}
            />
          </form>
        </Suspense>
      }
    </>
  )
}
